import {
  Box,
  Button,
  Flex,
  Header,
  OneColumnLayout,
  RadioGroup,
  spaces,
  Tooltip,
} from '@electricjs/arc';
import { useUpdateAssetMutation } from '@/redux/slices/assetApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import DeviceCard from './DeviceCard';
import { DeviceRetrieveAction } from './types';
import {
  AssetStatus,
  EntitlementSlugEnum,
  ProductTypeName,
} from '@electricjs/core_entity-client';
import SiteGroupField from './DeviceRetrieval/SiteGroupField';
import { ControlledCheckbox, OutlineChip } from '@common';
import DeviceRetrievalPolicy from './DeviceRetrievalPolicy';
import DeviceRetrievalTile from './DeviceRetrievalTile';
import EditAssetModal from '@/components/Assets/EditAssetModal';
import { useGlobalUI } from '@/components/GlobalUIProvider';

import { useEffect } from 'react';
import { Asset } from '@/types/assets';
import getProductTypeIcon from '@/components/Assets/helpers/getProductTypeIcon';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { selectOrganizationHasAddOn } from '@/redux/selectors/organizationAddOnSelectors';
import { useAppSelector } from '@/redux/store';
import { AddOnNames } from '@/types/addons';
import { useTranslation } from 'react-i18next';

type DeviceRetrievalProps = {
  asset: Asset;
  onNext: () => void;
  onPrevious: () => void;
  shouldDisablePrevious: boolean;
  isInternationalFlow: boolean;
  isEmployeeCaOrPr: boolean;
  onAssetSerialNumberUpdated: (assetId: string, serialNumber: string) => void;
};

const DeviceRetrieval = ({
  asset,
  onNext,
  onPrevious,
  shouldDisablePrevious,
  isInternationalFlow,
  isEmployeeCaOrPr,
  onAssetSerialNumberUpdated,
}: DeviceRetrievalProps) => {
  const { t } = useTranslation('common');
  const { openModal, closeModal } = useGlobalUI();
  const hasWarehousingEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Warehousing
  );
  const hasWarehousingAddOn = useAppSelector(
    selectOrganizationHasAddOn(AddOnNames.Warehousing)
  );
  const hasElectrolyteWarehousing =
    hasWarehousingEntitlement || hasWarehousingAddOn;
  const organizationId = useGetOrganizationId();
  const { control, setValue } = useFormContext();

  const deviceRetrieveAction = useWatch({ name: 'deviceRetrieveAction' });
  const siteValues = useWatch({ name: 'receiverSite' });
  const feeAccepted = useWatch({ name: 'feeAccepted' });

  const [updateAsset, { isLoading: isUpdatingAsset }] =
    useUpdateAssetMutation();

  // Electric only ships laptops out of offboarding via the QR program;
  // desktops and every other asset type are handled by the company directly.
  const isShippableAsset = asset.productType.name === ProductTypeName.Laptop;

  // The default US flow always offers the "Ship" tile; the international
  // flow only offers it when the employee is located in CA or PR.
  const shouldShowShipTile =
    isShippableAsset && (!isInternationalFlow || isEmployeeCaOrPr);
  const shouldShowWarehouseTile = isShippableAsset && hasElectrolyteWarehousing;

  // Electric can only handle a shipment it can identify the device by, so both
  // shipping options stay disabled until the asset has a serial number. The
  // admin can add one from the card's banner without leaving the flow.
  const hasSerialNumber = !!asset.serialNumber;

  // Form values persist across steps, so a previously selected "Ship" or
  // "Warehouse" action can outlive its tile when the employee country changes
  // or the asset turns out not to be shippable, and a shipping action can
  // outlive its eligibility when we land back here for an asset with no
  // serial number; clear it so Next isn't enabled with no visible selection.
  useEffect(() => {
    const shipTileUnavailable =
      !shouldShowShipTile && deviceRetrieveAction === DeviceRetrieveAction.Ship;
    const warehouseTileUnavailable =
      !shouldShowWarehouseTile &&
      deviceRetrieveAction === DeviceRetrieveAction.Warehouse;
    const shippingUnavailable =
      !hasSerialNumber &&
      (deviceRetrieveAction === DeviceRetrieveAction.Ship ||
        deviceRetrieveAction === DeviceRetrieveAction.Warehouse);

    if (
      shipTileUnavailable ||
      warehouseTileUnavailable ||
      shippingUnavailable
    ) {
      setValue('deviceRetrieveAction', null);
    }
  }, [
    shouldShowShipTile,
    shouldShowWarehouseTile,
    hasSerialNumber,
    deviceRetrieveAction,
    setValue,
  ]);

  const handleNext = async () => {
    const assetId = asset.id;
    let newAssetStatus;

    if (!assetId) {
      return;
    }

    if (deviceRetrieveAction === DeviceRetrieveAction.Ship) {
      onNext();
      return;
    }

    if (deviceRetrieveAction === DeviceRetrieveAction.Keep) {
      newAssetStatus = AssetStatus.Disposed;
    }

    if (deviceRetrieveAction === DeviceRetrieveAction.InPerson) {
      newAssetStatus = AssetStatus.InTransit;
    }

    if (deviceRetrieveAction === DeviceRetrieveAction.Warehouse) {
      onNext();
      return;
    }

    await updateAsset({
      organizationId,
      assetId,
      status: newAssetStatus,
    });
    onNext();
  };

  // Site selection for the Ship option happens here in Step 1 for the
  // default US flow; the international flow defers it to Step 2, where the
  // sender's country is known and CA/PR eligibility can be validated.
  const requiresSiteSelection =
    !isInternationalFlow &&
    deviceRetrieveAction === DeviceRetrieveAction.Ship &&
    !siteValues;
  const requiresFeeAcceptance =
    deviceRetrieveAction === DeviceRetrieveAction.Warehouse && !feeAccepted;
  const disableNext =
    !deviceRetrieveAction || requiresSiteSelection || requiresFeeAcceptance;

  const nextButtonDisabledReason = !disableNext
    ? undefined
    : !deviceRetrieveAction
      ? t('offboardings.deviceRetrieval.nextButtonDisabledSelectOption')
      : requiresSiteSelection
        ? t('offboardings.deviceRetrieval.nextButtonDisabledSelectSite')
        : requiresFeeAcceptance
          ? t('offboardings.deviceRetrieval.nextButtonDisabledAcceptFee')
          : t('offboardings.deviceRetrieval.nextButtonDisabledGeneric');

  // Lets the admin fill in a missing serial number without leaving the flow,
  // which unlocks the two shipping options.
  const openAddSerialNumberModal = () => {
    openModal(
      <EditAssetModal
        assetId={asset.id}
        name={asset.name}
        serialNumber={asset.serialNumber}
        serialNumberOnly
        onClose={closeModal}
        onAssetUpdated={({ serialNumber }) =>
          onAssetSerialNumberUpdated(asset.id, serialNumber)
        }
      />
    );
  };

  return (
    <OneColumnLayout>
      <Header title={t('offboardings.deviceRetrieval.title')} />
      <DeviceCard
        asset={asset}
        // The banner only makes sense for assets Electric can ship — a
        // serial number never unlocks anything for the other product types.
        onAddSerialNumber={
          isShippableAsset ? openAddSerialNumberModal : undefined
        }
      />
      <Controller
        name="deviceRetrieveAction"
        control={control}
        render={({ field }) => (
          <RadioGroup
            {...field}
            gap={spaces.space_m_rem}
            mt={spaces.space_2xl_rem}>
            {shouldShowWarehouseTile ? (
              <DeviceRetrievalTile
                deviceRetrieveAction={deviceRetrieveAction}
                value={DeviceRetrieveAction.Warehouse}
                optionText={t('offboardings.deviceRetrieval.options.warehouse')}
                priceText={isInternationalFlow ? undefined : '$95'}
                additionalPriceText={
                  isInternationalFlow ? undefined : ' + mo. fee'
                }
                icon="warehouse"
                disabled={!hasSerialNumber}
                promotionalChip={
                  isInternationalFlow ? undefined : (
                    <OutlineChip
                      id="warehousing-promotional-discount-chip"
                      label={t(
                        'offboardings.deviceRetrieval.promotionalDiscount'
                      )}
                      variant="small"
                    />
                  )
                }
              />
            ) : (
              <></>
            )}
            {shouldShowShipTile ? (
              <DeviceRetrievalTile
                deviceRetrieveAction={deviceRetrieveAction}
                value={DeviceRetrieveAction.Ship}
                optionText={t(
                  'offboardings.deviceRetrieval.options.shipToSite'
                )}
                priceText={isInternationalFlow ? undefined : '$95'}
                icon="building"
                disabled={!hasSerialNumber}
              />
            ) : (
              <></>
            )}
            <DeviceRetrievalTile
              deviceRetrieveAction={deviceRetrieveAction}
              value={DeviceRetrieveAction.InPerson}
              optionText={t('offboardings.deviceRetrieval.options.inPerson')}
              priceText={
                isInternationalFlow
                  ? undefined
                  : t('offboardings.deviceRetrieval.noCharge')
              }
              icon="person-with-box"
            />
            <DeviceRetrievalTile
              deviceRetrieveAction={deviceRetrieveAction}
              value={DeviceRetrieveAction.Keep}
              optionText={t('offboardings.deviceRetrieval.options.keep')}
              priceText={
                isInternationalFlow
                  ? undefined
                  : t('offboardings.deviceRetrieval.noCharge')
              }
              icon={getProductTypeIcon(asset.productType.name)}
            />
          </RadioGroup>
        )}
      />
      <DeviceRetrievalPolicy
        deviceRetrieveAction={deviceRetrieveAction}
        isInternationalFlow={isInternationalFlow}
      />
      {deviceRetrieveAction === DeviceRetrieveAction.Warehouse && (
        <Box mt={spaces.space_l_rem}>
          <ControlledCheckbox
            id="no-charger-fee-checkbox"
            name="feeAccepted"
            label={t('offboardings.deviceRetrieval.feeCheckboxLabel')}
            control={control}
          />
        </Box>
      )}
      {!isInternationalFlow &&
        deviceRetrieveAction === DeviceRetrieveAction.Ship && (
          <Box mt={spaces.space_l_rem}>
            <SiteGroupField
              name="receiverSite"
              maxWidth="50rem"
              shouldShowAddress
              required
            />
          </Box>
        )}
      <Flex mt="3rem" hAlignContent="between" gap="3.4rem" width="100%">
        <Button
          id="devices-retrieval-previous-button"
          variant="outline"
          onClick={onPrevious}
          disabled={isUpdatingAsset || shouldDisablePrevious}>
          {t('offboardings.deviceRetrieval.previousButton')}
        </Button>
        <Tooltip
          content={nextButtonDisabledReason}
          disabled={!nextButtonDisabledReason}
          hideWhenReferenceDisabled={false}
          placement="top">
          <Button
            id="devices-retrieval-next-button"
            onClick={handleNext}
            loading={isUpdatingAsset}
            disabled={disableNext}>
            {t('offboardings.deviceRetrieval.nextButton')}
          </Button>
        </Tooltip>
      </Flex>
    </OneColumnLayout>
  );
};

export default DeviceRetrieval;
